import { useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Home,
  UserPlus,
  Users,
  FileText,
  ShoppingCart,
  Calendar,
  GraduationCap,
  BookOpen,
  LogOut,
  Menu,
  X,
  ChevronRight,
  ChevronDown,
  User,
  Settings,
  UserCog,
  Wallet,
  Banknote,
  Calculator,
  Building2,
  Package,
  Upload,
  TrendingUp,
  ArrowRightLeft,
  FileCheck,
  Receipt,
  Send,
  BarChart3,
  BookOpenCheck,
  CreditCard,
  PiggyBank,
  Wrench,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { useTenant } from "../context/TenantContext";


// PORTAL PARA EL SOCIO
const navItemsPortal = [
  { path: "/dashboard", icon: Home, label: "Inicio", active: true },
  { path: "/dashboard/mi-perfil", icon: User, label: "Mi Perfil", active: true, socioOnly: true },
  { path: "/dashboard/alta-socios", icon: UserPlus, label: "Alta de Socio", active: true },
  { path: "/dashboard/guia-profesionales", icon: Users, label: "Guía Profesionales", active: true },
  { path: "/dashboard/tramites", icon: FileText, label: "Trámites", needsConfig: true },
  { path: "/dashboard/e-commerce", icon: ShoppingCart, label: "E-Commerce", needsConfig: true },
  { path: "/dashboard/eventos", icon: Calendar, label: "Eventos", needsConfig: true },
  { path: "/dashboard/e-learning", icon: GraduationCap, label: "E-Learning", needsConfig: true },
  { path: "/dashboard/biblioteca", icon: BookOpen, label: "Biblioteca", needsConfig: true },
];

// ADMINISTRACIÓN SOCIOS
const navItemsAdminSocios = [
  { path: "/dashboard/interno", icon: Settings, label: "Solicitudes", active: true },
  { path: "/dashboard/usuarios", icon: UserCog, label: "Gestión de Usuarios / Socios", active: true },
];

// ERP - FINANZAS
const navItemsFinanzas = [
  { path: "/dashboard/presupuesto", icon: Wallet, label: "Presupuesto", active: true },
];

// ERP - CONTABILIDAD
const navItemsContabilidad = [
  { path: "/dashboard/erp/ejercicios", icon: Calendar, label: "Ejercicios", active: true },
  { path: "/dashboard/erp/contabilidad", icon: BookOpenCheck, label: "Asientos / Plan Cuentas", active: true },
  { path: "/dashboard/erp/importacion-saldos", icon: Upload, label: "Importación", active: true },
  { path: "/dashboard/erp/reportes", icon: BarChart3, label: "Reportes", active: true },
  { path: "/dashboard/erp/ajuste-inflacion", icon: TrendingUp, label: "Ajuste Inflación (RT54)", active: true },
];

// ERP - ADMINISTRACIÓN (Tesorería)
const navItemsAdminERP = [
  { path: "/dashboard/erp/tesoreria", icon: Banknote, label: "Tesorería", active: true },
  { path: "/dashboard/erp/cotizaciones", icon: ArrowRightLeft, label: "Cotizaciones", active: true },
  { path: "/dashboard/erp/cobranzas", icon: Receipt, label: "Cobranzas", active: true },
  { path: "/dashboard/erp/pagos", icon: Send, label: "Pagos", active: true },
  { path: "/dashboard/erp/utes", icon: Users, label: "UTEs", active: true },
  { path: "/dashboard/erp/arqueo-caja", icon: Wallet, label: "Arqueo de Cajas", active: true },
  { path: "/dashboard/erp/fondo-fijo", icon: PiggyBank, label: "Fondo Fijo", active: true },
  { path: "/dashboard/erp/conciliacion-bancaria", icon: FileCheck, label: "Conciliación", active: true },
  { path: "/dashboard/erp/cuentas-corrientes", icon: CreditCard, label: "Cuentas Corrientes", active: true },
];

// ERP - FACTURACIÓN
const navItemsFacturacion = [
  { path: "/dashboard/erp/facturacion", icon: FileText, label: "Facturación", active: true },
];

// ERP - COMPRAS
const navItemsCompras = [
  { path: "/dashboard/erp/compras", icon: ShoppingCart, label: "Compras", active: true },
  { path: "/dashboard/erp/stock", icon: Package, label: "Stock", active: true },
];

const DashboardLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { config } = useTenant();
  const LOGO = config?.logo_url || "/logo-cao.svg";
  const NOMBRE = config?.nombre || "Cubiq";
  const NOMBRE_CORTO = config?.tenant_id?.toUpperCase() || "APP";
  const CP = config?.color_primario || "#2853B9";
  const CS = config?.color_secundario || "#1E3876";
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Todos los menús cerrados por defecto
  const [expandedSections, setExpandedSections] = useState({
    portal: false,
    adminSocios: false,
    finanzas: false,
    contabilidad: false,
    adminERP: false,
    facturacion: false,
    compras: false,
    superadmin: false,
  });

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const isActive = (path) => {
    if (path === "/dashboard") {
      return location.pathname === "/dashboard";
    }
    return location.pathname.startsWith(path);
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Permisos por rol
  const isAdmin = user?.role === "admin";
  const isController = user?.role === "controller";
  const isInterno = user?.role === "interno";
  
  // Admin: ve todo
  // Controller: solo ve Finanzas (Presupuesto)
  // Interno: ve todo ERP excepto Finanzas
  const canViewInterno = isInterno || isAdmin;  // Admin Socios
  const canViewFinanzas = isController || isAdmin;  // Solo Finanzas
  const canViewERPModules = isInterno || isAdmin;  // Contabilidad, Admin, Facturación, Compras
  const isSuperadmin = user?.role === "superadmin";

  const NavLink = ({ item }) => {
    const Icon = item.icon;
    const active = isActive(item.path);
    
    // Mi Perfil solo visible para socios confirmados
    if (item.socioOnly && user?.role !== "socio") {
      return null;
    }
    
    if (item.needsConfig) {
      return (
        <Link
          to={item.path}
          onClick={() => setSidebarOpen(false)}
          className="flex items-center gap-3 px-4 py-2.5 text-gray-400 hover:bg-gray-50 rounded-lg transition-colors"
        >
          <Icon className="w-4 h-4" />
          <span className="text-sm">{item.label}</span>
          <span className="ml-auto text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">Falta config.</span>
        </Link>
      );
    }

    return (
      <Link
        to={item.path}
        onClick={() => setSidebarOpen(false)}
        className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all duration-200 ${
          active
            ? "text-white shadow-md"
            : "text-gray-600 hover:bg-gray-100 hover:text-[#1E3876]"
        }`}
      >
        <Icon className="w-4 h-4" />
        <span className="text-sm font-medium">{item.label}</span>
        {active && <ChevronRight className="w-4 h-4 ml-auto" />}
      </Link>
    );
  };

  const SectionHeader = ({ label, section, expanded }) => (
    <button
      onClick={() => toggleSection(section)}
      className="flex items-center justify-between w-full px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider hover:bg-gray-50 rounded transition-colors"
    >
      <span>{label}</span>
      <ChevronDown className={`w-4 h-4 transition-transform ${expanded ? '' : '-rotate-90'}`} />
    </button>
  );

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="p-6 border-b">
        <Link to="/dashboard" className="flex items-center gap-3">
          <img src={LOGO} alt="CAO" className="h-10 w-auto" />
          <div>
            <h1 className="text-lg font-bold" style={{color: CS}}>{NOMBRE_CORTO}</h1>
            <p className="text-xs text-gray-500">{NOMBRE}</p>
          </div>
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-3 space-y-1">
        
        {/* PORTAL PARA EL SOCIO */}
        <SectionHeader label="Portal para el Socio" section="portal" expanded={expandedSections.portal} />
        {expandedSections.portal && (
          <div className="space-y-0.5 mb-3">
            {navItemsPortal.map((item) => (
              <NavLink key={item.path} item={item} />
            ))}
          </div>
        )}

        {/* ADMINISTRACIÓN SOCIOS */}
        {canViewInterno && (
          <>
            <SectionHeader label="Administración Socios" section="adminSocios" expanded={expandedSections.adminSocios} />
            {expandedSections.adminSocios && (
              <div className="space-y-0.5 mb-3">
                {navItemsAdminSocios.map((item) => (
                  <NavLink key={item.path} item={item} />
                ))}
              </div>
            )}
          </>
        )}

        {/* ERP */}
        {(isAdmin || isInterno || isController) && (
          <>
            <div className="pt-2 pb-1">
              <p className="px-4 text-xs font-bold text-[#1E3876] uppercase tracking-wider border-b pb-2 mb-2">ERP</p>
            </div>

            {/* FINANZAS */}
            {canViewFinanzas && (
              <>
                <SectionHeader label="Finanzas" section="finanzas" expanded={expandedSections.finanzas} />
                {expandedSections.finanzas && (
                  <div className="space-y-0.5 mb-2">
                    {navItemsFinanzas.map((item) => (
                      <NavLink key={item.path} item={item} />
                    ))}
                  </div>
                )}
              </>
            )}

            {/* CONTABILIDAD - Solo interno/admin */}
            {canViewERPModules && (
              <>
                <SectionHeader label="Contabilidad" section="contabilidad" expanded={expandedSections.contabilidad} />
                {expandedSections.contabilidad && (
                  <div className="space-y-0.5 mb-2">
                    {navItemsContabilidad.map((item) => (
                      <NavLink key={item.path} item={item} />
                    ))}
                  </div>
                )}
              </>
            )}

            {/* ADMINISTRACIÓN (Tesorería) - Solo interno/admin */}
            {canViewERPModules && (
              <>
                <SectionHeader label="Administración" section="adminERP" expanded={expandedSections.adminERP} />
                {expandedSections.adminERP && (
                  <div className="space-y-0.5 mb-2">
                    {navItemsAdminERP.map((item) => (
                      <NavLink key={item.path} item={item} />
                    ))}
                  </div>
                )}
              </>
            )}

            {/* FACTURACIÓN - Solo interno/admin */}
            {canViewERPModules && (
              <>
                <SectionHeader label="Facturación" section="facturacion" expanded={expandedSections.facturacion} />
                {expandedSections.facturacion && (
                  <div className="space-y-0.5 mb-2">
                    {navItemsFacturacion.map((item) => (
                      <NavLink key={item.path} item={item} />
                    ))}
                  </div>
                )}
              </>
            )}

            {/* COMPRAS - Solo interno/admin */}
            {canViewERPModules && (
              <>
                <SectionHeader label="Compras" section="compras" expanded={expandedSections.compras} />
                {expandedSections.compras && (
                  <div className="space-y-0.5 mb-2">
                    {navItemsCompras.map((item) => (
                      <NavLink key={item.path} item={item} />
                    ))}
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* SUPERADMIN */}
        {isSuperadmin && (
          <>
            <div className="pt-2 pb-1">
              <p className="px-4 text-xs font-bold text-orange-600 uppercase tracking-wider border-b border-orange-200 pb-2 mb-2">Superadmin</p>
            </div>
            <SectionHeader label="Administracion Cubiq" section="superadmin" expanded={expandedSections.superadmin} />
            {expandedSections.superadmin && (
              <div className="space-y-0.5 mb-2">
                <NavLink item={{ path: "/dashboard/superadmin/tenants", icon: Wrench, label: "Gestion de Empresas", active: true }} />
              </div>
            )}
          </>
        )}
      </nav>

      {/* User */}
      <div className="p-4 border-t bg-gray-50">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-[#2853B9] flex items-center justify-center text-white">
            <User className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">
              {user?.nombre || user?.email}
            </p>
            <p className="text-xs text-gray-500 capitalize">{user?.role}</p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
          onClick={handleLogout}
        >
          <LogOut className="w-4 h-4 mr-2" />
          Cerrar Sesión
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b shadow-sm">
        <div className="flex items-center justify-between px-4 py-3">
          <Link to="/dashboard" className="flex items-center gap-2">
            <img src={LOGO} alt="CAO" className="h-8 w-auto" />
            <span className="font-bold" style={{color: CS}}>{NOMBRE_CORTO}</span>
          </Link>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </Button>
        </div>
      </div>

      {/* Mobile Sidebar Overlay */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="lg:hidden fixed inset-0 z-40 bg-black/50"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Mobile Sidebar */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.aside
            initial={{ x: -280 }}
            animate={{ x: 0 }}
            exit={{ x: -280 }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            className="lg:hidden fixed top-0 left-0 bottom-0 z-50 w-72 bg-white shadow-xl"
          >
            <SidebarContent />
          </motion.aside>
        )}
      </AnimatePresence>

      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex lg:flex-col lg:w-72 lg:fixed lg:inset-y-0 bg-white border-r shadow-sm">
        <SidebarContent />
      </aside>

      {/* Main Content */}
      <main className="lg:pl-72 pt-16 lg:pt-0">
        <div className="p-4 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default DashboardLayout;
